import { useState, useEffect, useRef } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useInfrastructureCache } from "../../hooks/useInfrastructureCache";
import { useVmCost } from "../../hooks/useVmCost";
import { Server, Tag, Search, ChevronDown } from "lucide-react";
import YearRangeFilter from "./YearRangeFilter";

const PeriodCost = () => {
  const [selectedVmIds, setSelectedVmIds] = useState([1, 2, 3, 4, 5, 6, 7]);
  const [isVmDropdownOpen, setIsVmDropdownOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("vms");
  const [searchTerm, setSearchTerm] = useState("");
  const [last12MonthsData, setLast12MonthsData] = useState([]);
  const [currentRange, setCurrentRange] = useState({ startMonth: 1, startYear: 2026, endMonth: 12, endYear: 2026 });

  const { getTotalCostForVms, totalCostData, loading } = useVmCost();
  const { data: infrastructure } = useInfrastructureCache();

  const getLast12MonthsRange = () => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    return {
      startMonth: startDate.getMonth() + 1,
      startYear: startDate.getFullYear(),
      endMonth: today.getMonth() + 1,
      endYear: today.getFullYear(),
    };
  };

  useEffect(() => {
    const fetchLast12Months = async () => {
      const range = getLast12MonthsRange();
      const result = await getTotalCostForVms(
        selectedVmIds,
        range.startMonth,
        range.startYear,
        range.endMonth,
        range.endYear,
      );
      if (result?.success && result?.data?.vmDetails) {
        setLast12MonthsData(result.data.vmDetails);
      }
    };
    fetchLast12Months();
  }, [selectedVmIds]);

  useEffect(() => {
    getTotalCostForVms(
      selectedVmIds,
      currentRange.startMonth,
      currentRange.startYear,
      currentRange.endMonth,
      currentRange.endYear,
    );
  }, [selectedVmIds, currentRange]);

  const handleRangeChange = (startMonth, startYear, endMonth, endYear) => {
    setCurrentRange({ startMonth, startYear, endMonth, endYear });
  };

  const getVmsWithDetails = () => {
    if (!infrastructure?.vms) return [];
    return infrastructure.vms.map((vm) => ({
      ...vm,
      tags: infrastructure.vmTags
        .filter((relation) => relation.vmId === vm.id)
        .map((relation) =>
          infrastructure.tags.find((tag) => tag.id === relation.tagId)
        )
        .filter((tag) => tag),
    }));
  };

  const getTagsWithCounts = () => {
    if (!infrastructure?.tags) return [];
    return infrastructure.tags.map((tag) => ({
      ...tag,
      vmCount: infrastructure.vmTags.filter(
        (relation) => relation.tagId === tag.id,
      ).length,
      vms: infrastructure.vmTags
        .filter((relation) => relation.tagId === tag.id)
        .map((relation) =>
          infrastructure.vms.find((vm) => vm.id === relation.vmId)
        )
        .filter((vm) => vm),
    }));
  };

  const vmsWithTags = getVmsWithDetails();
  const tagsWithCounts = getTagsWithCounts();

  const filteredVms = vmsWithTags.filter((vm) =>
    vm.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const filteredTags = tagsWithCounts.filter(
    (tag) =>
      tag.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tag.key.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleVmSelect = (vm) => {
    setSelectedVmIds([vm.id]);
    setIsVmDropdownOpen(false);
    setSearchTerm("");
  };

  const handleTagSelect = (tag) => {
    const vmIds = tag.vms.map((vm) => vm.id);
    setSelectedVmIds(vmIds);
    setIsVmDropdownOpen(false);
    setSearchTerm("");
  };

  const COLORS = [
    "#dc2626", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6",
    "#ec489a", "#06b6d4", "#84cc16", "#ef4444", "#14b8a6",
  ];

  const pieData =
    totalCostData?.vmDetails?.map((vm, index) => ({
      name: vm.vmName,
      value: vm.totalAmount,
      color: COLORS[index % COLORS.length],
    })) || [];

  const totalAmount = totalCostData?.totalAmount || 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / totalAmount) * 100).toFixed(1);
      return (
        <div className="bg-white text-primary-dark p-3 rounded-lg shadow-lg border border-primary-cyan">
          <p className="font-semibold">{payload[0].name}</p>
          <p className="text-primary-cyan font-bold">${payload[0].value.toFixed(2)}</p>
          <p className="text-sm text-gray-500">{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap justify-center gap-2 mt-2">
        {payload.map((entry, index) => (
          <div
            key={`legend-${index}`}
            className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/10"
          >
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-white text-xs">{entry.value}</span>
            <span className="text-primary-cyan text-xs font-semibold">
              ${entry.payload.value.toFixed(0)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const generateLast12MonthsLabels = () => {
    const labels = [];
    const today = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      labels.push(`${monthNames[date.getMonth()]} ${date.getFullYear()}`);
    }
    return labels;
  };

  const last12MonthsLabels = generateLast12MonthsLabels();

  return (
    <div className="p-2 sm:p-4 md:px-7">
      <div className="bg-primary-dark rounded-2xl shadow-xl overflow-hidden">
        <div className="p-3 sm:p-4 md:p-6 border-b border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">Cost Overview</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setIsVmDropdownOpen(!isVmDropdownOpen)}
                  className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 bg-primary-cyan hover:bg-primary-cyan/80 rounded-lg transition-all text-white font-medium w-full sm:w-auto text-sm sm:text-base"
                >
                  <span>Select VM / Tag</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${isVmDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isVmDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 right-0 sm:right-0 sm:left-auto w-full sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
                    <div className="flex border-b border-gray-200 bg-gray-50">
                      <button
                        onClick={() => setActiveCategory("vms")}
                        className={`flex-1 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                          activeCategory === "vms"
                            ? "text-primary-cyan border-b-2 border-primary-cyan bg-white"
                            : "text-gray-600 hover:text-primary-dark"
                        }`}
                      >
                        <Server size={14} />
                        Virtual Machines
                      </button>
                      <button
                        onClick={() => setActiveCategory("tags")}
                        className={`flex-1 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                          activeCategory === "tags"
                            ? "text-primary-cyan border-b-2 border-primary-cyan bg-white"
                            : "text-gray-600 hover:text-primary-dark"
                        }`}
                      >
                        <Tag size={14} />
                        Tags
                      </button>
                    </div>

                    <div className="p-3 border-b border-gray-200">
                      <div className="relative">
                        <Search
                          size={14}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="text"
                          placeholder={`Search ${activeCategory === "vms" ? "VMs..." : "tags..."}`}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-primary-dark placeholder-gray-400 focus:outline-none focus:border-primary-cyan focus:ring-1 focus:ring-primary-cyan"
                        />
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {activeCategory === "vms" ? (
                        filteredVms.length === 0 ? (
                          <div className="p-8 text-center text-gray-500">
                            <Server size={32} className="mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No VMs found</p>
                          </div>
                        ) : (
                          filteredVms.map((vm) => (
                            <button
                              key={vm.id}
                              onClick={() => handleVmSelect(vm)}
                              className="w-full text-left px-3 sm:px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 hover:border-l-4 hover:border-l-primary-cyan"
                            >
                              <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                                <div className="flex-1">
                                  <p className="font-medium text-primary-dark text-sm">{vm.name}</p>
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        vm.status === "PowerState/deallocated"
                                          ? "bg-red-100 text-red-600"
                                          : "bg-green-100 text-green-600"
                                      }`}
                                    >
                                      {vm.status === "PowerState/deallocated" ? "Stopped" : "Running"}
                                    </span>
                                    {vm.tags.slice(0, 2).map((tag) => (
                                      <span
                                        key={tag.id}
                                        className="text-xs px-2 py-0.5 rounded-full bg-primary-cyan/10 text-primary-cyan"
                                      >
                                        {tag.value}
                                      </span>
                                    ))}
                                    {vm.tags.length > 2 && (
                                      <span className="text-xs text-gray-400">+{vm.tags.length - 2}</span>
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400">{vm.vmType || "N/A"}</span>
                              </div>
                            </button>
                          ))
                        )
                      ) : filteredTags.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <Tag size={32} className="mx-auto mb-2 opacity-40" />
                          <p className="text-sm">No tags found</p>
                        </div>
                      ) : (
                        filteredTags.map((tag) => (
                          <button
                            key={tag.id}
                            onClick={() => handleTagSelect(tag)}
                            className="w-full text-left px-3 sm:px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 hover:border-l-4 hover:border-l-primary-cyan"
                          >
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Tag size={14} className="text-primary-cyan" />
                                  <p className="font-medium text-primary-dark text-sm">{tag.value}</p>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Key: {tag.key}</p>
                              </div>
                              <span className="text-xs bg-primary-cyan/10 px-2 py-1 rounded-full text-primary-cyan font-medium">
                                {tag.vmCount} VM{tag.vmCount !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <YearRangeFilter onRangeChange={handleRangeChange} />
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64 sm:h-96">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-white/60 text-sm sm:text-base">Loading cost data...</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
              <div className="flex-1 space-y-4">
                <div className="text-center">
                  <p className="text-white/60 text-sm sm:text-base">Total Cost</p>
                  <p className="text-white text-2xl sm:text-3xl md:text-4xl font-bold">
                    ${totalAmount.toFixed(2)}
                  </p>
                </div>
                <div className="h-64 sm:h-80 md:h-96">
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="45%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          labelLine={false}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          content={renderLegend}
                          verticalAlign="bottom"
                          height={100}
                          wrapperStyle={{ paddingTop: "10px" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-white/40 text-sm">No data available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1">
                <div className="text-center mb-3 sm:mb-4">
                  <p className="text-white/80 font-bold text-xl sm:text-2xl md:text-3xl">Last 12 Months Costs</p>
                </div>
                <div className="overflow-x-auto max-h-[350px] sm:max-h-[450px] overflow-y-auto">
                  <div className="min-w-[500px] sm:min-w-0">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-primary-dark">
                        <tr className="border-b border-white/20">
                          <th className="text-left text-base sm:text-xl font-bold py-3 px-2 sm:px-3 text-white/60">VM Name</th>
                          <th className="text-right py-3 px-2 sm:px-3 text-white/60 text-base sm:text-xl font-bold">Total Cost</th>
                          <th className="text-right py-3 px-2 sm:px-3 text-white/60 text-base sm:text-xl font-bold">Monthly Avg</th>
                         </tr>
                      </thead>
                      <tbody>
                        {last12MonthsData.length > 0 ? (
                          last12MonthsData.map((vm) => {
                            const monthlyAvg = vm.totalAmount / 12;
                            return (
                              <tr
                                key={vm.vmId}
                                className="border-b border-white/10 hover:bg-white/5 transition-colors"
                              >
                                <td className="py-3 px-2 sm:px-3 text-sm sm:text-base md:text-xl text-white font-medium break-words">{vm.vmName}</td>
                                <td className="py-3 px-2 sm:px-3 text-right text-primary-cyan font-semibold text-sm sm:text-base md:text-xl">
                                  ${vm.totalAmount.toFixed(2)}
                                </td>
                                <td className="py-3 px-2 sm:px-3 text-right text-white/60 text-sm sm:text-base md:text-xl">
                                  ${monthlyAvg.toFixed(2)}
                                </td>
                               </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-8 text-white/40 text-sm">
                              No data available for last 12 months
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
                    <span className="text-white/40 text-sm sm:text-base">
                      Total VMs: {last12MonthsData.length}
                    </span>
                    <span className="text-white/40 text-sm sm:text-base">
                      Total Cost:{" "}
                      <span className="text-primary-cyan font-bold">
                        ${last12MonthsData.reduce((sum, vm) => sum + vm.totalAmount, 0).toFixed(2)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeriodCost;